import React, { useEffect, useState, useMemo } from 'react';
import { loadQuestions } from '../../services/csvLoader';
import { generateDocument } from '../../services/docxExporter';
import { Section, ProgressBar, Sidebar } from './Dashboard';
import { ResetButton, ExportButton } from './ActionButtons';
import { ActionsPanel } from './ActionsPanel';


const HealthCheck = () => {
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadQuestions()
            .then(data => {
                setQuestions(data);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setError("Failed to load questions.");
                setLoading(false);
            });
    }, []);

    const handleAnswerChange = (id, newAnswer) => {
        setAnswers(prev => {
            // If the new answer is the same as the old one, unselect it
            if (prev[id]?.value === newAnswer.value && !newAnswer.response) {
                const newAnswers = { ...prev };
                delete newAnswers[id];
                return newAnswers;
            }
            return {
                ...prev,
                [id]: newAnswer,
            };
        });
    };

    const handleReset = () => {
        setAnswers({});
        window.scrollTo(0, 0);
    };

    const handleExport = () => {
        const clientName = answers['0.1']?.value || 'Client';
        generateDocument(clientName, answers);
    };


    // Group questions by section
    const sections = useMemo(() => {
        return questions.reduce((acc, q) => {
            if (!acc[q.section]) {
                acc[q.section] = [];
            }
            acc[q.section].push(q);
            return acc;
        }, {});
    }, [questions]);

    // Calculate Progress
    const totalQuestions = questions.length;
    const answeredCount = Object.keys(answers).filter(k => answers[k] && answers[k].value.trim() !== '').length;

    // Calculate Section Completion
    const sectionStatus = useMemo(() => {
        const status = {};
        Object.entries(sections).forEach(([title, qs]) => {
            const isComplete = qs.every(q => {
                // Check if answer exists and is not empty
                const ans = answers[q.id];
                return ans && ans.value.trim() !== '';
            });
            status[title] = isComplete;
        });
        return status;
    }, [sections, answers]);

    if (loading) return <div>Loading questions...</div>;
    if (error) return <div style={{ color: 'red' }}>{error}</div>;

    return (
        <div style={{ fontFamily: 'Arial, sans-serif', background: '#f5f7fa', minHeight: '100vh' }}>
            <ProgressBar total={totalQuestions} completed={answeredCount} />

            <div style={{
                maxWidth: '1500px', // Widen the container for the new panel
                margin: '0 auto',
                padding: '0 20px 40px',
                display: 'flex',
                gap: '40px',
                alignItems: 'flex-start'
            }}>
                <Sidebar
                    sections={Object.keys(sections)}
                    completedSections={sectionStatus}
                />

                <div style={{ flex: 1, minWidth: 0 }}>
                    <h1 style={{ textAlign: 'center', marginBottom: '30px', color: '#333' }}>Health & Safety - Health Check</h1>

                    {Object.entries(sections).map(([sectionTitle, sectionQuestions]) => (
                        <div id={sectionTitle} key={sectionTitle} style={{ marginBottom: '30px', scrollMarginTop: '100px' }}>
                            <Section
                                title={sectionTitle}
                                questions={sectionQuestions}
                                answers={answers}
                                onAnswerChange={handleAnswerChange}
                            />
                        </div>
                    ))}

                    <div style={{
                        position: 'sticky',
                        bottom: '20px',
                        background: 'white',
                        padding: '1rem',
                        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderRadius: '8px',
                        zIndex: 100,
                        gap: '15px'
                    }}>
                        <ResetButton onReset={handleReset} />
                        <ExportButton onExport={handleExport} />
                    </div>
                </div>

                <ActionsPanel questions={questions} answers={answers} />
            </div>
        </div>
    );
};

export default HealthCheck;